
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, OperatorFunction, throwError, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import * as AppStoreSelectors from 'apps/console/src/app/root-store/app-store/selectors';
import { IDocumentFile, HttpErrorMessage, IUnavailableDocumentFile } from '@aston/foundation';
import { TranslateService } from '@ngx-translate/core';

import { mapToSalesConditions } from '../functions';
import { SalesConditionsDocumentApi } from '../models/api';
import { SalesConditionsDocument } from '../models';

@Injectable({
	providedIn: 'root'
})
export class DocumentFileService {

	private http = inject(HttpClient)
	private store = inject(Store)
	private sanitizer =inject(DomSanitizer)
	private translateService =inject(TranslateService)

	toastTranslationKeyPrefix = 'Toasts.File';

	catchDocumentError<T>(): OperatorFunction<T, T> {
		return catchError((error: HttpErrorMessage) => {

			if (error.key.includes('404')) {
				error.key = error.key.replace('404', 'DocumentNotFound');
				error.translated = this.translateService.instant(error.key);
			}

			return throwError(error);
		});
	}

	orUnavailableDocumentWithPath<T>(unavailableDocumentBase: IDocumentFile): OperatorFunction<T, T | IUnavailableDocumentFile> {
		return catchError(error => {

			if (error.key && error.key.includes('404') && unavailableDocumentBase) {
				return of({ ...unavailableDocumentBase, error });
			}

			return of(error);
		});
	}

	getFilesOfDocument(documentFile: IDocumentFile): Observable<IDocumentFile[]> {
		// Since Task 37690: Back / fusionner doc/docfile dans console
		// DocumentFile is deprecated and url can be directly deduced from document's id. 		
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			map(config => [{
				...documentFile,
				url: `${config.apiUrl}/api/documents/${documentFile.id}/content`
			}]),
		);
	}

	getLocalFileURL(documentFile: IDocumentFile): Observable<IDocumentFile> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get(`${config.apiUrl}/api/documents/${documentFile.id}/content`, { observe: 'response', responseType: 'blob' })),
			map(response => ({
				...documentFile,
				name : documentFile.name!==null?documentFile.name:  response.headers.get('content-disposition').match(/filename=([^;]*);/)[0],
				url: this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(response.body))
			})),
			this.orUnavailableDocumentWithPath(documentFile),
			this.catchDocumentError()
		);
	}

	getLatestSalesConditionsDocument(): Observable<SalesConditionsDocument> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<SalesConditionsDocumentApi>(`${config.apiUrl}/api/Documents/cgv/latest`)),
			map(result => mapToSalesConditions(result))
		);
	}
}
